import React, { useState, useCallback, useEffect, useRef, CSSProperties } from 'react';
import { useTranslation } from 'next-i18next';
import { BuildingOffice2Icon, LinkIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';

import { Button, IconButton, SkeletonTextLoading } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';
import { useEmployersService } from '@restworld/data-services';

import { useNearbyVenues } from './use-nearby-venues';
import ShareLinkModal from './share-link-modal';

const VISIBLE_COUNT = 3;
const DISMISS_DURATION_MS = 300;
const STORAGE_KEY = 'referral-dismissed-venues';

/** Read dismissed IDs from localStorage */
function loadDismissedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    // ignore
  }
  return new Set();
}

/** Persist dismissed IDs to localStorage */
function saveDismissedIds(ids: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

/**
 * Suggested venues section -- shows nearby restaurants (from Google Places)
 * that are NOT already in Restworld's database. The employer can share their
 * referral link with these venues.
 *
 * Always shows up to 3 venues. When one is dismissed (with a fade/slide
 * transition), the next venue from the remaining pool takes its place and the
 * section scrolls into view so all 3 venues are clearly visible.
 */
export function ReferralSuggestedVenues() {
  const { t } = useTranslation('referral-page');
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => loadDismissedIds());
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // The Google Nearby Search behind this section is a billed Pro-SKU call: fetch only
  // once the section actually enters the viewport (it sits at the bottom of the page).
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || hasBeenVisible) return;
    if (typeof IntersectionObserver === 'undefined') {
      setHasBeenVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setHasBeenVisible(true);
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasBeenVisible]);

  const { venues, isLoading } = useNearbyVenues({ enabled: hasBeenVisible });

  const employersService = useEmployersService();
  const { data: referralData } = useQuery({
    queryKey: ['referral-link'],
    // Unwraps inside the queryFn, not through `select`: `select` runs per observer
    // and never touches the cache, so it would store the AxiosResponse under a key
    // every other consumer reads as the payload. See available-credits.tsx.
    queryFn: async () => (await employersService.getReferralLink()).data,
  });
  const referralLink = referralData?.referral_link ?? '';

  // Pool of non-dismissed venues, capped to VISIBLE_COUNT
  const visibleVenues = venues.filter((v) => !dismissedIds.has(v.placeId)).slice(0, VISIBLE_COUNT);
  const hasVisibleVenues = !isLoading && visibleVenues.length > 0;

  const handleDismiss = useCallback(
    (placeId: string) => {
      if (dismissingId) return; // prevent double-click during animation

      // Start the exit animation
      setDismissingId(placeId);

      // After animation completes, move venue to dismissed set & scroll into view
      setTimeout(() => {
        setDismissedIds((prev) => {
          const next = new Set(prev).add(placeId);
          saveDismissedIds(next);
          return next;
        });
        setDismissingId(null);

        // Scroll the section into view so all venues are visible
        requestAnimationFrame(() => {
          sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }, DISMISS_DURATION_MS);
    },
    [dismissingId]
  );

  return (
    <SectionContainer ref={sectionRef}>
      <style jsx global>{`
        .venue-row .venue-info {
          grid-area: info;
        }
        .venue-row .venue-cta {
          grid-area: cta;
        }
        .venue-row .venue-dismiss {
          grid-area: dismiss;
        }
        @media (min-width: 1024px) {
          .venue-row {
            grid-template-columns: 1fr auto auto;
            grid-template-rows: auto;
            grid-template-areas: 'info cta dismiss';
            align-items: center;
          }
        }
      `}</style>
      <SectionTitle>{t('suggested_venues_title')}</SectionTitle>
      <VenuesCard>
        <CardDescription>
          {hasVisibleVenues ? t('suggested_venues_description') : t('suggested_venues_empty')}
        </CardDescription>

        {isLoading && <LoadingSkeleton />}

        {!isLoading && visibleVenues.length > 0 && (
          <VenuesList>
            {visibleVenues.map((venue) => (
              <VenueRowAnimated key={venue.placeId} isDismissing={dismissingId === venue.placeId}>
                <VenueInfo className="venue-info">
                  <VenueName>{venue.name}</VenueName>
                  <VenueType>
                    <BuildingOffice2Icon className="w-4 h-4 text-[#666676] flex-shrink-0" />
                    <span>{venue.type}</span>
                  </VenueType>
                </VenueInfo>
                <ShareButtonWrapper className="venue-cta">
                  <Button
                    variant="secondary-restworld"
                    StartIcon={LinkIcon}
                    fullwidth
                    onClick={() => setIsShareModalOpen(true)}
                  >
                    {t('share_invitation_link')}
                  </Button>
                </ShareButtonWrapper>
                <DismissWrapper className="venue-dismiss">
                  <IconButton
                    Icon={XMarkIcon}
                    ariaLabel="Dismiss"
                    variant="neutral"
                    size="md"
                    onClick={() => handleDismiss(venue.placeId)}
                  />
                </DismissWrapper>
              </VenueRowAnimated>
            ))}
          </VenuesList>
        )}
      </VenuesCard>

      <ShareLinkModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        referralLink={referralLink}
        source="suggested_venues"
      />
    </SectionContainer>
  );
}

/** Loading skeleton for the venue rows */
function LoadingSkeleton() {
  return (
    <VenuesList>
      {[1, 2, 3].map((i) => (
        <VenueRowAnimated key={i} isDismissing={false}>
          <div className="flex flex-col gap-2 flex-1">
            <SkeletonTextLoading className="!w-48 !h-5" width="rect-box" />
            <SkeletonTextLoading className="!w-28 !h-4" width="rect-box" />
          </div>
          <SkeletonTextLoading className="!w-44 !h-12" width="rect-box" />
        </VenueRowAnimated>
      ))}
    </VenuesList>
  );
}

export default ReferralSuggestedVenues;

// Layout Components
const SectionContainer = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col items-center w-full py-10 px-4 lg:px-20 gap-5', className)}
      {...props}
    />
  )
);
SectionContainer.displayName = 'SectionContainer';

const SectionTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h2
    className={cn('text-xl font-semibold text-[#1C1C33] leading-[1.21] self-start lg:self-center', className)}
    {...props}
  />
);

const VenuesCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'w-full bg-[#F5F5F6] border border-[#DEDEE1] rounded-2xl px-4 pt-10 pb-4 lg:px-10 lg:py-10 flex flex-col justify-start items-center gap-5',
      className
    )}
    style={{ boxShadow: '0px 2px 4px 0px rgba(0, 0, 26, 0.05)' }}
    {...props}
  />
);

const CardDescription = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-base text-[#1C1C33] leading-normal text-left lg:text-center', className)} {...props} />
);

const VenuesList = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4 lg:gap-3 w-full', className)} {...props} />
);

/**
 * CSS Grid venue row with dismiss animation.
 *   Mobile  -> 2 rows: [info dismiss] / [cta]
 *   Desktop -> 1 row:  [info cta dismiss]
 */
const venueRowBaseStyle: CSSProperties = {
  display: 'grid',
  gap: '24px',
  gridTemplateColumns: '1fr auto',
  gridTemplateRows: 'auto auto',
  gridTemplateAreas: '"info dismiss" "cta cta"',
  alignItems: 'start',
  transition: `opacity ${DISMISS_DURATION_MS}ms ease, transform ${DISMISS_DURATION_MS}ms ease, max-height ${DISMISS_DURATION_MS}ms ease, padding ${DISMISS_DURATION_MS}ms ease, margin ${DISMISS_DURATION_MS}ms ease, border-width ${DISMISS_DURATION_MS}ms ease`,
  opacity: 1,
  maxHeight: '250px',
  transform: 'translateX(0)',
};

const venueRowDismissingStyle: CSSProperties = {
  opacity: 0,
  transform: 'translateX(40px)',
  maxHeight: 0,
  paddingTop: 0,
  paddingBottom: 0,
  borderWidth: 0,
  overflow: 'hidden',
};

interface VenueRowAnimatedProps extends React.HTMLAttributes<HTMLDivElement> {
  isDismissing: boolean;
}

const VenueRowAnimated = ({ isDismissing, className, style, ...props }: VenueRowAnimatedProps) => (
  <div
    className={cn('venue-row bg-[#FAFAFA] rounded-2xl p-4 lg:px-6 lg:py-5 border border-[#DEDEE1] w-full', className)}
    style={{
      ...venueRowBaseStyle,
      ...(isDismissing ? venueRowDismissingStyle : {}),
      ...style,
    }}
    {...props}
  />
);

const VenueInfo = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2 min-w-0', className)} {...props} />
);

const VenueName = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-base font-semibold text-[#1C1C33] truncate', className)} {...props} />
);

const VenueType = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('flex items-center gap-0.5 text-sm font-medium text-[#666676]', className)} {...props} />
);

const ShareButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full lg:w-auto', className)} {...props} />
);

const DismissWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-center', className)} {...props} />
);
